import { getDayTimeStepLabels, HOUR_MINUTES, QUARTER_MINUTES } from 'utils/datetime/dayTimeSteps'
import { getElplanHourSlotStarts, getElplanHourValue, groupElplanDraftByHour } from './draft'
import styles from './ElplanEditor.module.less'
import type {
  ElplanDraft,
  ElplanEditorRow,
  ElplanHourReading,
  ElplanRowRead,
  ElplanShownReading,
  ElplanSlotFaults,
  ElplanSlotUnit,
} from './ElplanEditor.types'
import { toElplanVolumeCellText } from './volume'

export function buildElplanEditorRows(
  draft: ElplanDraft,
  mode: ElplanSlotUnit,
  expandedHourStarts: ReadonlySet<ISODateTime>
): ElplanEditorRow[] {
  const hours = groupElplanDraftByHour(draft)
  const hourLabels = getDayTimeStepLabels(
    hours.map(({ hourStart }) => hourStart),
    HOUR_MINUTES
  )
  const quarterLabelByStart = getQuarterLabelByStart(draft)

  return hours.flatMap(({ hourStart, slotStarts }, hourIndex): ElplanEditorRow[] => {
    const isExpanded = mode === 'quarter' && expandedHourStarts.has(hourStart)
    const hourRow: ElplanEditorRow = { kind: 'hour', start: hourStart, label: hourLabels[hourIndex], isExpanded }
    if (!isExpanded) {
      return [hourRow]
    }

    return [
      hourRow,
      ...slotStarts.map(
        (start): ElplanEditorRow => ({
          kind: 'quarter',
          start,
          label: quarterLabelByStart.get(start) ?? '',
        })
      ),
    ]
  })
}

export function getElplanRowClassName(row: ElplanEditorRow): string {
  return row.kind === 'hour' ? styles.ElplanEditor_HourRow : styles.ElplanEditor_QuarterRow
}

export function readElplanRow(
  row: ElplanEditorRow,
  mode: ElplanSlotUnit,
  draft: ElplanDraft,
  faults: ElplanSlotFaults,
  changedSlotStarts: ReadonlySet<ISODateTime>
): ElplanRowRead {
  const reading: ElplanHourReading | ElplanShownReading =
    row.kind === 'hour'
      ? getElplanHourValue(row.start, draft, faults.shownReadingByStart)
      : (faults.shownReadingByStart[row.start] ?? { kind: 'blank' })
  const fault = reading.kind === 'fault' ? reading.fault : undefined
  const rowSlotStarts = row.kind === 'hour' ? getElplanHourSlotStarts(row.start, draft) : [row.start]
  const hasEmptyValues =
    row.kind === 'hour' && rowSlotStarts.some((start) => faults.shownReadingByStart[start]?.kind === 'blank')
  const isChanged = rowSlotStarts.some((start) => changedSlotStarts.has(start))
  const isSummary = row.kind === 'hour' && mode === 'quarter'
  if (!isSummary) {
    const text = draft.textByStart[row.start] ?? ''
    return { isSummary, valueText: text, displayText: text, hasEmptyValues, fault, isChanged }
  }

  const valueText = reading.kind === 'value' ? toElplanVolumeCellText(reading.volumeMw) : ''
  const displayText = reading.kind === 'partial' ? toElplanVolumeCellText(reading.shownMeanMw) : valueText
  return { isSummary, valueText, displayText, hasEmptyValues, fault, isChanged }
}

function getQuarterLabelByStart(draft: ElplanDraft): Map<ISODateTime, string> {
  if (draft.resolution !== 'quarter') {
    return new Map()
  }

  const labels = getDayTimeStepLabels(draft.slotStarts, QUARTER_MINUTES)
  return new Map(draft.slotStarts.map((start, index) => [start, labels[index]]))
}
